import type { Characters, Item, ItemName, PageProps, WithExclusion } from '@ps-smart-cp/ppfw-client';
import { MasterRangeSearchField, MasterSearchField, withRerender } from '@ps-smart-cp/ppfw-client';
import { genMstColumns } from '../components';
import { mstListSearchFieldConfig } from '../item-config';
import type { MstRegistration } from '../models';
import { MstListSearch } from '../models';
import { hoMstFetch, hoMstListRequest } from '../repositories';
import type { MstRangeSearchProps, MstSearchDialogProps, MstSearchProps } from './MstSearchDialogTmpl';
import { MstSearchDialogTmpl } from './MstSearchDialogTmpl';

function InnerComponent({
  mstName,
  path,
  ...restProps
}: MstSearchDialogProps & PageProps & { mstName: ItemName; path: string }) {
  return (
    <MstSearchDialogTmpl
      mstName={mstName}
      columns={genMstColumns()}
      items={mstListSearchFieldConfig}
      listRequestFunction={hoMstListRequest(path)()}
      initMst={MstListSearch.baseInit}
      {...restProps}
    />
  );
}

const Dialog = withRerender(InnerComponent, 'Dialog');

const highOrderMstSearchTmpl = <HT, HK extends keyof HT>(mstName: ItemName, path: string) => {
  return function Search<T extends Pick<HT, K>, K extends HK, I extends Record<K, Item<Characters>>>(
    props: MstSearchProps<T, K, I>,
  ) {
    return (
      <MasterSearchField<T, K, I, MstRegistration>
        getMaster={async (code) => hoMstFetch(path)()({ code })}
        {...props}
      >
        {(setIsOpen, setMaster, getMst) => (
          <Dialog
            setIsOpen={setIsOpen}
            setMaster={setMaster}
            mstName={mstName}
            getMaster={getMst}
            path={path}
          />
        )}
      </MasterSearchField>
    );
  };
};

const highOrderMstRangeSearchTmpl = <HT, HK extends keyof HT>(mstName: ItemName, path: string) => {
  return function Search<T extends Pick<HT, K>, K extends HK, I extends Record<K, Item<WithExclusion>>>(
    props: MstRangeSearchProps<T, K, I>,
  ) {
    return (
      <MasterRangeSearchField<T, K, I, MstRegistration>
        getMaster={async (code) => hoMstFetch(path)()({ code })}
        {...props}
      >
        {(setIsOpen, setMaster, getMst) => (
          <Dialog
            setIsOpen={setIsOpen}
            setMaster={setMaster}
            mstName={mstName}
            getMaster={getMst}
            path={path}
          />
        )}
      </MasterRangeSearchField>
    );
  };
};

export { highOrderMstRangeSearchTmpl, highOrderMstSearchTmpl };
